import axios from "axios";
import jwt_decode from "jwt-decode";
import configJson from "../../config.json";

// Records why the browser logged the user out, so a forced logout can be
// explained later. The reason can't be sent at logout time (the token is
// being thrown away, and a dropped network is often the cause), so it is
// kept in localStorage and posted to /auth/logout-reason by the first
// authenticated request after the next login. It lands in log01 as
// "FE logout reason for user N".

const STORAGE_KEY = "crm:lastLogoutReason";
// When one tab logs out the others follow, sometimes minutes later. The first
// tab's reason is the cause, so later tabs only add a short note to it until
// the next login sends it. Older than this, it is a leftover nobody sent.
const KEEP_FIRST_MS = 6 * 60 * 60 * 1000;
const MAX_LATER_NOTES = 5;
// Tells the tabs of one browser apart in a report.
const TAB_ID = Math.random().toString(36).slice(2, 8);
const PAGE_LOADED_AT = new Date().toISOString();
const HEARTBEAT_MS = 10000;
// A heartbeat gap this long means the machine slept or the tab was frozen.
const SLEEP_GAP_MS = 30000;

const normalizedUrlBase = (configJson?.urlBase || "").replace(/\/$/, "");
const reportUrl = `${normalizedUrlBase}/auth/logout-reason`;
const reportClient = axios.create();

let lastHeartbeat = Date.now();
let lastWake = null;
let lastVisible = Date.now();
let flushing = false;

if (typeof window !== "undefined") {
  window.setInterval(() => {
    const now = Date.now();
    const gap = now - lastHeartbeat;
    if (gap > SLEEP_GAP_MS) {
      lastWake = { at: new Date(now).toISOString(), asleepSeconds: Math.round(gap / 1000) };
    }
    lastHeartbeat = now;
  }, HEARTBEAT_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      lastVisible = Date.now();
    }
  });
}

const stripQuery = url => (typeof url === "string" ? url.split("?")[0] : undefined);

const readToken = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem("persist:demo4-auth") || "null");
    const token = (stored?.authToken || "").replace(/['"]+/g, "").replace(/^Bearer\s+/i, "");
    if (!token) {
      return null;
    }
    const { exp, iat, id } = jwt_decode(token);
    return { exp, iat, id };
  } catch (e) {
    return null;
  }
};

// Pull the useful parts out of an axios error or request config.
export const describeRequest = errorOrConfig => {
  if (!errorOrConfig) {
    return {};
  }
  const config = errorOrConfig.config || errorOrConfig;
  const response = errorOrConfig.response;
  const data = response?.data;
  const message = typeof data === "string" ? data : data?.message || data?.error;
  return {
    method: config?.method ? config.method.toUpperCase() : undefined,
    url: stripQuery(config?.url),
    status: response?.status,
    responseMessage: message ? String(message).slice(0, 300) : undefined,
    errorMessage: errorOrConfig.message ? String(errorOrConfig.message).slice(0, 300) : undefined,
    errorCode: errorOrConfig.code,
  };
};

export const recordLogoutReason = (reason, details = {}) => {
  try {
    const now = Date.now();
    const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (existing && now - Date.parse(existing.at) < KEEP_FIRST_MS) {
      const later = Array.isArray(existing.later) ? existing.later : [];
      if (later.length < MAX_LATER_NOTES) {
        later.push({ reason, tabId: TAB_ID, at: new Date(now).toISOString(), page: window.location.pathname });
      }
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...existing, later, laterTabs: (existing.laterTabs || 0) + 1 })
      );
      return;
    }
    const entry = {
      reason,
      ...details,
      tabId: TAB_ID,
      pageLoadedAt: PAGE_LOADED_AT,
      at: new Date(now).toISOString(),
      page: window.location.pathname,
      online: navigator.onLine,
      visibility: document.visibilityState,
      secondsSinceTabVisible: Math.round((now - lastVisible) / 1000),
      lastWake,
      token: readToken(),
      userAgent: navigator.userAgent,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (e) {
    // Never let logging get in the way of the logout itself.
  }
};

// Called with a valid Bearer header; sends any pending reason once.
export const flushLogoutReason = authorizationHeader => {
  if (flushing || !authorizationHeader) {
    return;
  }
  let pending;
  try {
    pending = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return;
  }
  if (!pending) {
    return;
  }
  flushing = true;
  reportClient
    .post(reportUrl, JSON.parse(pending), { headers: { Authorization: authorizationHeader } })
    .then(() => window.localStorage.removeItem(STORAGE_KEY))
    .catch(() => {})
    .finally(() => {
      flushing = false;
    });
};
